/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   collectionJobService.ts
 *     Feature         :   139-collection-job-console
 *     Description     :   API client + React Query hooks for Collection Jobs
 *     Constitution    :   v1.12.0 | Principle XXXVII (Code Headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-02
 * =========================================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import type {
  CollectionJobStatus,
  CollectionRun,
  CollectionRunsResponse,
  StartJobResponse,
} from '../../types/collectionJob';

// ============================================================================
// Query Key Factory
// ============================================================================

export const COLLECTION_JOB_KEYS = {
  all: ['operations', 'collection-jobs'] as const,
  status: () => [...COLLECTION_JOB_KEYS.all, 'status'] as const,
  runs: (filters: Record<string, unknown>) => [...COLLECTION_JOB_KEYS.all, 'runs', filters] as const,
  runDetail: (runId: string) => [...COLLECTION_JOB_KEYS.all, 'run', runId] as const,
};

// ============================================================================
// Polling Intervals
// ============================================================================

/** Polling interval when job is running (15 seconds per FR-006, NFR-003) */
export const COLLECTION_JOB_POLL_INTERVAL = 15_000;

/** Polling interval when idle (60 seconds) */
const IDLE_POLL_INTERVAL = 60_000;

/** Stale time for run history (30 seconds) */
const RUNS_STALE_TIME = 30_000;

/** Refresh interval for run history (60 seconds) */
const RUNS_REFRESH_INTERVAL = 60_000;

// ============================================================================
// API Functions
// ============================================================================

/** GET /api/operations/collection-jobs/status */
export async function getCollectionJobStatus(): Promise<CollectionJobStatus> {
  const response = await apiClient.get('/api/operations/collection-jobs/status');
  return response.data.data;
}

/** POST /api/operations/collection-jobs/start */
export async function startCollectionJob(): Promise<StartJobResponse> {
  const response = await apiClient.post('/api/operations/collection-jobs/start');
  return response.data.data;
}

/** GET /api/operations/collection-jobs/runs */
export async function getCollectionRuns(limit: number, offset: number, tier?: string): Promise<CollectionRunsResponse> {
  const response = await apiClient.get('/api/operations/collection-jobs/runs', {
    params: { limit, offset, ...(tier ? { tier } : {}) },
  });
  return response.data.data;
}

/** GET /api/operations/collection-jobs/runs/:runId */
export async function getCollectionRunById(runId: string): Promise<CollectionRun> {
  const response = await apiClient.get(`/api/operations/collection-jobs/runs/${encodeURIComponent(runId)}`);
  return response.data.data;
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook: Current collection job status with dynamic polling (T019, FR-006)
 * Polls every 15s when running, every 60s when idle
 */
export function useCollectionJobStatus() {
  return useQuery({
    queryKey: COLLECTION_JOB_KEYS.status(),
    queryFn: getCollectionJobStatus,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'running' ? COLLECTION_JOB_POLL_INTERVAL : IDLE_POLL_INTERVAL;
    },
    staleTime: 10_000,
  });
}

/**
 * Hook: Start collection job mutation (T010)
 * Invalidates status + runs on success
 */
export function useStartCollectionJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startCollectionJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_JOB_KEYS.status() });
      queryClient.invalidateQueries({ queryKey: COLLECTION_JOB_KEYS.all });
    },
  });
}

/**
 * Hook: Paginated collection runs (T014)
 */
export function useCollectionRuns(limit: number, offset: number, tier?: string) {
  return useQuery({
    queryKey: COLLECTION_JOB_KEYS.runs({ limit, offset, tier }),
    queryFn: () => getCollectionRuns(limit, offset, tier),
    staleTime: RUNS_STALE_TIME,
    refetchInterval: RUNS_REFRESH_INTERVAL,
  });
}

/**
 * Hook: Single collection run detail (T017)
 */
export function useCollectionRunById(runId: string | null) {
  return useQuery({
    queryKey: COLLECTION_JOB_KEYS.runDetail(runId ?? ''),
    queryFn: () => getCollectionRunById(runId!),
    enabled: !!runId,
    staleTime: RUNS_STALE_TIME,
  });
}
